import { useState } from 'react';
import getConfig from 'next/config';

import { Modal } from '../../../../../../components/ToolkitV2/Modal/Modal';
import {
  LOADING_COLOUR,
  Loading,
} from '../../../../../../components/Toolkit/Loading/Loading';
import { BUTTON_TYPE } from '../../../../../../components/Toolkit/Button/SharedButton';
import { theme } from '../../../../../../helpers/Theme';
import { useRevealPhoneNumber } from '../../../../../../components/PageWrappers/SharedComponents/RevealPhoneNumber/useRevealPhoneNumber';
import {
  getCallHref,
  getCallLabel,
  getOfType,
} from '../../../../../../components/Toolkit/ShowPhoneNumberButton/ShowPhoneNumberButton';
import type { RevealPhoneNumberProps } from '../../../../../../components/PageWrappers/SharedComponents/RevealPhoneNumber/RevealPhoneNumber';

import * as Styled from './ShowNumberBottomSheet.styled';

function ShowNumberBottomSheet({
  adId,
  size = 'SMALL',
  clickSource = '',
  isPrimaryButton = false,
  campaignId,
  sectionName,
  sellerType,
  price,
  disabled,
  id,
  children,
}: RevealPhoneNumberProps) {
  const {
    publicRuntimeConfig: { CDN_STATIC_ASSETS },
  } = getConfig();

  const { handleReveal: revealPhoneNumber, handleOnCall } =
    useRevealPhoneNumber({
      adId,
      clickSource,
      campaignId,
      sectionName,
      sellerType,
      price,
    });

  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  async function handleReveal() {
    setIsLoading(true);
    const number = await revealPhoneNumber();
    setIsLoading(false);
    if (number) {
      setPhoneNumber(number);
      setIsOpen(true);
    }
  }

  const ofType = getOfType(isPrimaryButton);

  return (
    <>
      <Styled.ShowPhoneNumberButton
        ofType={ofType}
        size={size}
        disabled={disabled || isLoading}
        id={id}
        data-testid="view-phone-number"
        onClick={handleReveal}
      >
        {isLoading ? (
          <Loading
            cdnUrl={CDN_STATIC_ASSETS}
            color={isPrimaryButton ? LOADING_COLOUR.LIGHT : LOADING_COLOUR.DARK}
          />
        ) : (
          children ?? 'Show number'
        )}
      </Styled.ShowPhoneNumberButton>

      <Modal
        isOpen={isOpen}
        onRequestClose={() => setIsOpen(false)}
        modalClassName="show-number"
        padding={theme.spacing.M24}
        left={0}
        right={0}
        bottom={0}
        top={null}
        offScreenTransform="translateX(0) translateY(100%)"
        onScreenTransform="translateX(0) translateY(0)"
        borderRadius={`${theme.borderRadius.default} ${theme.borderRadius.default} 0 0`}
        contentLabel="Show Number"
      >
        <Styled.SModalHeader
          text="Show number"
          modalName="Show Number"
          onRequestClose={() => setIsOpen(false)}
          weight="bold"
          size="L18"
          color="GREY_DARKER"
          forwardedAs="h2"
        />
        <Styled.CallPhoneNumberButtonLink
          ofType={BUTTON_TYPE.PRIMARY}
          href={getCallHref(phoneNumber)}
          data-testid="call-phone-number"
          onClick={handleOnCall}
        >
          {getCallLabel(phoneNumber)}
        </Styled.CallPhoneNumberButtonLink>
      </Modal>
    </>
  );
}

export { ShowNumberBottomSheet };
